import React, {useCallback, useState} from 'react';
import {
    Button,
    DropdownItem,
    DropdownV2,
    Icon,
    noop,
    PriorityIcon,
    useDropdown,
    useKeyboardNavigation
} from '@priority-software/priority-style-react';
import {DropdownItemData} from '@priority-software/priority-style-react/dist/components/DropdownV2/ChooseDropdown/ChooseDropdown';
import {onBpmAction} from '../../../../../../_shared/GWTBridge/invoke/bpm/bpmMain';
import styles from './styles.module.scss';
import {BpmActions} from '../../../../../../_shared/GWTBridge/invoke/bpm/types';
import {createDataTestId} from '../../../../../../utils/dataTestId';
import {IconWithTooltip} from './IconWithTooltip';
import {texts} from '../../texts';

enum DropdownActions {
    copy = 'copy',
    paste = 'paste',
    report = 'report'
}

const getDropdownOptions = () => [
    {label: texts.copy_label, value: DropdownActions.copy},
    {label: texts.paste_label, value: DropdownActions.paste},
    {label: texts.report_label, value: DropdownActions.report}
];

const DD_styles = {width: 200};

export type BPMFooterProps = {
    addNewStatus: () => void;
    showStatusSettings: () => void;
    fromStatus: string;
    toStatus: string;
    onOpenRules: (fromStatus: string, toStatus: string) => void;
};

export const BPMFooter = (props: BPMFooterProps) => {
    const {addNewStatus, showStatusSettings, fromStatus, toStatus, onOpenRules} = props;
    const {isOpen, toggleOpen, closeDropdown} = useDropdown({onClose: noop});
    const [dropdownOptions] = useState(getDropdownOptions());

    const {focusedIndex, handleNavigationKeys} = useKeyboardNavigation({
        optionsCount: dropdownOptions.length,
        defaultFocusedIndex: 0,
        isOpen
    });

    const deleteItem = useCallback(() => {
        const action = Boolean(fromStatus && toStatus)
            ? BpmActions.deletePath
            : !!toStatus
            ? BpmActions.deleteStatus
            : null;
        onBpmAction(action);
    }, [fromStatus, toStatus]);

    const onOptionSelect = useCallback((option: DropdownItemData) => {
        if (option.value === DropdownActions.copy) {
            onBpmAction(BpmActions.copyStatus);
        } else if (option.value === DropdownActions.paste) {
            onBpmAction(BpmActions.paste);
        } else if (option.value === DropdownActions.report) {
            onBpmAction(BpmActions.print);
        }
    }, []);

    const notEnoughData = !toStatus && !fromStatus;

    const showAutomation = () => {
        onOpenRules(fromStatus, toStatus);
    };

    return (
        <div className="bpm-footer-wrapper">
            <div className={styles.component}>
                <Button
                    skin="primary"
                    onClick={addNewStatus}
                    style={{width: 32}}
                    icon={
                        <IconWithTooltip
                            icon={PriorityIcon.newAdd}
                            componentClasses={styles.plusIcon}
                        />
                    }
                    componentClasses={styles.addBtn}
                    {...createDataTestId('add-bpm-status', true)}
                />

                <IconWithTooltip
                    {...createDataTestId('add-bpm-path', true)}
                    componentClasses={styles.icon}
                    onClick={() => onBpmAction(BpmActions.addPath)}
                    icon={PriorityIcon.conversionPath}
                    tooltip={texts.add_path}
                    disabled={!!fromStatus}
                />

                <IconWithTooltip
                    {...createDataTestId('open-bpm-status-settings', true)}
                    componentClasses={styles.icon}
                    disabled={!toStatus || !!fromStatus}
                    onClick={showStatusSettings}
                    icon={PriorityIcon.instant_mix}
                    tooltip={texts.set_properties}
                />

                <IconWithTooltip
                    {...createDataTestId('open-bpm-color-picker', true)}
                    componentClasses={styles.icon}
                    onClick={() => onBpmAction(BpmActions.changeColor)}
                    icon={PriorityIcon.colors}
                    disabled={!toStatus || !!fromStatus}
                    tooltip={texts.set_color}
                />

                <IconWithTooltip
                    {...createDataTestId('automation-bpm-open', true)}
                    componentClasses={styles.icon}
                    onClick={showAutomation}
                    icon={PriorityIcon.automations}
                    disabled={notEnoughData}
                    tooltip={texts.set_rules}
                />

                <IconWithTooltip
                    {...createDataTestId('delete-bpm-element', true)}
                    componentClasses={styles.icon}
                    disabled={notEnoughData}
                    onClick={deleteItem}
                    icon={PriorityIcon.delete}
                    tooltip={texts.delete_label}
                />

                <DropdownV2
                    isOpen={isOpen}
                    onKeyDown={handleNavigationKeys}
                    onClose={closeDropdown}
                    trigger={
                        <Icon
                            componentClasses={styles.icon}
                            disabled={false}
                            onClick={toggleOpen}
                            icon={PriorityIcon.threeDots}
                        />
                    }
                >
                    <div style={DD_styles}>
                        {dropdownOptions.map((item, index) => {
                            return (
                                <DropdownItem
                                    key={item.value}
                                    data={item}
                                    onSelect={onOptionSelect}
                                    isSelected={false}
                                    isFocused={focusedIndex === index}
                                />
                            );
                        })}
                    </div>
                </DropdownV2>
            </div>
        </div>
    );
};
